import {
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  GetCountriesParams,
  GetCountriesResponse,
  GetCountryResponse,
  GetDoka360IsActiveInCountryResponse,
  ProblemDetails
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a list of countries<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice<br>welcome.doka.com/onboarding-dokathreesixty
 */
export const useGetCountriesHook = () => {
        const getCountries = useCustomWelcomeClient<GetCountriesResponse>();

        return useCallback((
    params?: GetCountriesParams,
 signal?: AbortSignal
) => {


        return getCountries(
          {url: `/countries`, method: 'GET',
        params, signal
    },
          );
        }, [getCountries])
      }




export const getGetCountriesQueryKey = (params?: GetCountriesParams,) => {
    return [
    `/countries`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetCountriesQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError = ErrorType<ProblemDetails>>(params?: GetCountriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCountriesQueryKey(params);

  const getCountries =  useGetCountriesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>> = ({ signal }) => getCountries(params, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetCountriesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>>
export type GetCountriesQueryError = ErrorType<ProblemDetails>


export function useGetCountries<TData = Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError = ErrorType<ProblemDetails>>(
 params: undefined |  GetCountriesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCountries<TData = Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetCountriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCountries<TData = Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetCountriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetCountries<TData = Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError = ErrorType<ProblemDetails>>(
 params?: GetCountriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountriesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetCountriesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns a country by its country code (ISO 3166-1 alpha-2 code)<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetCountryByCodeHook = () => {
        const getCountryByCode = useCustomWelcomeClient<GetCountryResponse>();

        return useCallback((
    countryCode: string,
 signal?: AbortSignal
) => {


        return getCountryByCode(
          {url: `/countries/${countryCode}`, method: 'GET', signal
    },
          );
        }, [getCountryByCode])
      }




export const getGetCountryByCodeQueryKey = (countryCode: string,) => {
    return [
    `/countries/${countryCode}`
    ] as const;
    }


export const useGetCountryByCodeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError = ErrorType<ProblemDetails>>(countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCountryByCodeQueryKey(countryCode);

  const getCountryByCode =  useGetCountryByCodeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>> = ({ signal }) => getCountryByCode(countryCode, signal);





   return  { queryKey, queryFn, enabled: countryCode !== null && countryCode !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetCountryByCodeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>>
export type GetCountryByCodeQueryError = ErrorType<ProblemDetails>


export function useGetCountryByCode<TData = Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCountryByCode<TData = Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCountryByCode<TData = Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetCountryByCode<TData = Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetCountryByCodeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetCountryByCodeQueryOptions(countryCode,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns whether Doka 360 is active in the specified country.<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetDoka360IsActiveInCountryHook = () => {
        const getDoka360IsActiveInCountry = useCustomWelcomeClient<GetDoka360IsActiveInCountryResponse>();

        return useCallback((
    countryCode: string,
 signal?: AbortSignal
) => {


        return getDoka360IsActiveInCountry(
          {url: `/countries/${countryCode}/doka360/is-active`, method: 'GET', signal
    },
          );
        }, [getDoka360IsActiveInCountry])
      }




export const getGetDoka360IsActiveInCountryQueryKey = (countryCode: string,) => {
    return [
    `/countries/${countryCode}/doka360/is-active`
    ] as const;
    }


export const useGetDoka360IsActiveInCountryQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError = ErrorType<ProblemDetails>>(countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDoka360IsActiveInCountryQueryKey(countryCode);

  const getDoka360IsActiveInCountry =  useGetDoka360IsActiveInCountryHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>> = ({ signal }) => getDoka360IsActiveInCountry(countryCode, signal);





   return  { queryKey, queryFn, enabled: countryCode !== null && countryCode !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetDoka360IsActiveInCountryQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>>
export type GetDoka360IsActiveInCountryQueryError = ErrorType<ProblemDetails>


export function useGetDoka360IsActiveInCountry<TData = Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDoka360IsActiveInCountry<TData = Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDoka360IsActiveInCountry<TData = Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetDoka360IsActiveInCountry<TData = Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError = ErrorType<ProblemDetails>>(
 countryCode: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetDoka360IsActiveInCountryHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetDoka360IsActiveInCountryQueryOptions(countryCode,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






